/**
 * Cliente del CRUD de `routing_config` (Fase 33). Spec en
 * `docs/start-plan/33-llm-model-routing-from-backoffice.md`.
 */
import { get, patchJsonAdmin } from './_core.js';

export type TaskType =
  | 'llm.main'
  | 'llm.corrections'
  | 'llm.distill'
  // Split F2 del distiller de práctica (2026-07-12): radar de errores+focos
  // y estimación de nivel CEFR, cada uno con su dial de modelo.
  | 'llm.radar'
  | 'llm.estimate'
  // Placement (entrevista de nivel del onboarding), separado de llm.estimate
  // el 2026-10-02: el estimate pasó a Jev y el placement necesita texto.
  | 'llm.placement'
  // Free-talk skill judge (W5, 2026-10-07).
  | 'llm.skills'
  // Scenario debrief judge, split from llm.distill (plan 46m, 2026-10-09).
  | 'llm.debrief'
  | 'llm.translate'
  // Split del cajón de sastre llm.translate (2026-07-17).
  | 'llm.explain'
  | 'llm.suggest'
  | 'llm.scenario'
  | 'llm.lookup'
  // Selector del significado según la frase (encargo 63, 2026-10-08).
  | 'llm.meaning'
  // Juez del ejemplo corto de una palabra guardada (card #580, 2026-10-21).
  | 'llm.example_judge'
  | 'llm.game'
  // translate-it attempt check, before the game reply (2026-10-13).
  | 'llm.game_check'
  // translate-it solutions, «No sé» / «Ver solución» (2026-10-17).
  | 'llm.game_solution'
  // Live-recall selector (Jev), 2026-10-15.
  | 'llm.recall_select'
  | 'llm.news_classify'
  | 'llm.recall'
  | 'llm.reflect'
  | 'llm.safety'
  | 'llm.summarize'
  | 'llm.theory'
  // Typed decisions (Jev via TypeSafe's systemone) — correction verifier + turn signals.
  | 'llm.verify'
  | 'stt'
  | 'tts'
  | 'voice'
  | 'pronunciation'
  | 'phone.call'
  | 'video.analyze'
  // Tier del modelo S2S del modo realtime (full vs mini) — routing only, el
  // billing va por débito directo (realtime-plan §5.2).
  | 'voice.realtime';

export interface ModelOverride {
  provider: string;
  model: string;
}

export type RoutingOverrides = Partial<Record<TaskType, ModelOverride>>;

export interface CatalogPricing {
  /** LLM. */
  inputPer1M?: number;
  outputPer1M?: number;
  /** TTS. */
  perMChar?: number;
  /** STT. */
  perMinute?: number;
}

export interface CatalogEntry {
  id: string;
  provider: string;
  displayName: string;
  supports: readonly TaskType[];
  tier: 'fast' | 'balanced' | 'frontier';
  status: 'stable' | 'experimental' | 'deprecated';
  notes?: string;
  pricing: CatalogPricing;
  addedAt: string;
}

export interface TaskRoute {
  provider: string;
  model: string;
  serviceKind: string;
}

export interface RoutingConfigResponse {
  overrides: RoutingOverrides;
  updatedAt: string;
  updatedBy: string | null;
  defaults: Record<TaskType, TaskRoute>;
  moduleDefaults: Record<string, Partial<Record<TaskType, TaskRoute>>>;
  catalog: readonly CatalogEntry[];
}

export interface RoutingAuditEntry {
  id: string;
  changedAt: string;
  changedByUserId: string | null;
  beforeConfig: RoutingOverrides | null;
  afterConfig: RoutingOverrides;
}

export async function fetchRoutingConfig(): Promise<RoutingConfigResponse> {
  const res = await get<RoutingConfigResponse>(`/admin/routing`);
  return res.data;
}

export async function patchRoutingConfig(
  overrides: RoutingOverrides,
): Promise<{ overrides: RoutingOverrides }> {
  return patchJsonAdmin<{ overrides: RoutingOverrides }>(`/admin/routing`, { overrides });
}

export async function fetchRoutingAudit(): Promise<{ entries: RoutingAuditEntry[] }> {
  const res = await get<{ entries: RoutingAuditEntry[] }>(`/admin/routing/audit`);
  return res.data;
}
